// NDB sales pricing — resolve the configured housing to a price from the
// cpq2_ndb_pricing table (the "NDB Sales Pricing - 2026 Baseline" workbook).
//
// The price list is keyed on exactly four values:
//
//   housing type    OT | HTB | HTC | BT      Hinge is split bolted/clamped
//   design          STD, CL, RN, LN, AC, ACLN, EAS, EASAC, EASLN, PF, …
//   housing size    the FIRST number of the model designation (FAH30/34 -> 30)
//   housing height  the SECOND number (FAH30/34 -> 34)
//
// All four come off the SELECTED row of the Housing Options table (BURNER_TYPE),
// which is also where the Housing Validation op reads them to build the model
// designation. Size deliberately reuses that op's own expression — CELL_OD, +4
// for Box-Type — so the size we price on is always the same number that appears
// before the slash in the model. (Nothing here parses the model string itself;
// the NDB Model column of the workbook is ignored.)
//
// If any of the four is missing, or the price list has no row for them, or its
// rows disagree, NO price is returned and the caller leaves the price alone:
// the item keeps its default base price and the sales-order line keeps the
// placeholder amount. A wrong price is far worse than no price.

import type { AnswerState } from "../types";

const API_BASE = "/api";

/** The four values the price list is keyed on. */
export type NdbPriceKey = {
  housingType: "OT" | "HTB" | "HTC" | "BT";
  design: string;
  size: number;
  height: number;
};

export type NdbPriceResult =
  | { matched: true; price: number; reference: string | null; key: NdbPriceKey }
  | { matched: false; reason: string; key: NdbPriceKey | null };

// Housing Options "Housing Type" cell (BURNER_NAME) -> price-list housing code.
// Hinge Type needs the Bolted/Clamped answer to pick a side; T-Type and anything
// else has no pricing on file.
const HOUSING_CODE: Record<string, "OT" | "BT" | "HINGE"> = {
  "o-type": "OT",
  "box-type": "BT",
  "box type": "BT",
  "hinge type": "HINGE",
};

/** Parse a table cell that should hold a positive number ('30', '30"', ' 34 '). */
function cellNumber(v: unknown): number | null {
  const s = String(v ?? "").replace(/["″,\s]/g, "");
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** The selected Housing Options row index (SELECTION), or "" when none is picked. */
function selectedRow(answers: AnswerState): string {
  const sel = String(answers["SELECTION/SELECTION"] ?? "").trim();
  if (sel) return sel;
  // Replacement / BYOB orders configure a single hand-entered row (index 1) and
  // never set SELECTION, so fall back to row 1 when its cells are populated.
  return answers["BURNER_TYPE/BURNER_NAME_1"] != null ? "1" : "";
}

/** Has a housing been picked yet? Before that there is nothing to price, which the Sales
 *  Price panel shows as a quiet dash rather than the "No list price" warning. */
export function hasSelectedHousing(answers: AnswerState): boolean {
  const sel = selectedRow(answers);
  // selectedRow falls back to row 1 when its cell merely exists, and a new configuration
  // has that cell blank -- a housing is picked only once the row names one.
  return sel !== "" && String(answers[`BURNER_TYPE/BURNER_NAME_${sel}`] ?? "").trim() !== "";
}

/** Build the price-list key from the current answers, or explain what's missing. */
export function buildNdbPriceKey(
  answers: AnswerState
): { key: NdbPriceKey } | { key: null; reason: string } {
  const sel = selectedRow(answers);
  if (!sel) return { key: null, reason: "no housing selected in Housing Options" };

  const burnerName = String(answers[`BURNER_TYPE/BURNER_NAME_${sel}`] ?? "").trim();
  const code = HOUSING_CODE[burnerName.toLowerCase()];
  if (!code) {
    return { key: null, reason: `no pricing on file for housing type "${burnerName || "(blank)"}"` };
  }

  let housingType: NdbPriceKey["housingType"];
  if (code === "HINGE") {
    // Bolted vs clamped is a different housing type in the price list, so an
    // unanswered Bolted/Clamped means we cannot know which column to read.
    const bolted = answers["MOUNTING/BOLTED"] === true;
    const clamped = answers["MOUNTING/CLAMPED"] === true;
    if (bolted) housingType = "HTB";
    else if (clamped) housingType = "HTC";
    else return { key: null, reason: "Hinge Type needs Bolted or Clamped answered" };
  } else {
    housingType = code;
  }

  const design = String(answers[`BURNER_TYPE/DESIGN_${sel}`] ?? "").trim().toUpperCase();
  if (!design) return { key: null, reason: "no Design on the selected housing row" };

  const cellOd = cellNumber(answers[`BURNER_TYPE/CELL_OD_${sel}`]);
  if (cellOd == null) return { key: null, reason: "no Cell OD on the selected housing row" };
  // Same expression the Housing Validation op uses for the model's size part:
  // Box-Type adds 4 to the cell OD, everything else uses it as-is. A BYOB Box-Type
  // prices on the Width typed on the housing row instead (Kevin, 2026-10-30: "price off of
  // the width not for the cell + 4"); a blank Width is Cell OD + 4, as in the model.
  const boxWidth = cellNumber(answers[`BURNER_TYPE/BOX_WIDTH_${sel}`]);
  const byobBox = burnerName === "Box-Type" && answers["ORDER_TYPE/BYOB"] === true;
  const size = byobBox && boxWidth != null ? boxWidth : burnerName === "Box-Type" ? cellOd + 4 : cellOd;

  const height = cellNumber(answers[`BURNER_TYPE/DIMENSIONS_${sel}`]);
  if (height == null) return { key: null, reason: "no Housing Height on the selected housing row" };

  return { key: { housingType, design, size, height } };
}

// ANSWERS ALREADY GIVEN, per key. The Sales Price panel looks the price up while the user is
// still configuring, and Save and Submit used to ask the very same question again as the
// first network call of its wait. Kevin, 2026-10-16: cut the time from open to submit
// without giving up reliability.
//
// Only a DEFINITIVE answer is kept -- the endpoint replied, matched or not. A network failure
// or an error status is never cached, so a blip during configuring cannot stick and cost the
// line its price; the submit simply asks again. Entries expire so a session left open across
// a price-list change re-reads it.
const ANSWER_TTL_MS = 10 * 60 * 1000;
const answered = new Map<string, { at: number; result: NdbPriceResult }>();
const inFlight = new Map<string, Promise<NdbPriceResult>>();

/** Look the key up against /api/ndb-price. Never throws — a lookup failure is
 *  reported as unmatched so it can never block a submit. */
export async function lookupNdbPrice(answers: AnswerState): Promise<NdbPriceResult> {
  const built = buildNdbPriceKey(answers);
  if (!built.key) return { matched: false, reason: built.reason, key: null };
  const key = built.key;
  const qs = new URLSearchParams({
    housingType: key.housingType,
    design: key.design,
    size: String(key.size),
    height: String(key.height),
  });
  const cacheKey = qs.toString();
  const hit = answered.get(cacheKey);
  if (hit && Date.now() - hit.at < ANSWER_TTL_MS) return hit.result;
  const pending = inFlight.get(cacheKey);
  if (pending) return pending;
  const p = fetchNdbPrice(key, qs).finally(() => inFlight.delete(cacheKey));
  inFlight.set(cacheKey, p);
  return p;
}

async function fetchNdbPrice(key: NdbPriceKey, qs: URLSearchParams): Promise<NdbPriceResult> {
  const remember = (result: NdbPriceResult): NdbPriceResult => {
    answered.set(qs.toString(), { at: Date.now(), result });
    return result;
  };
  try {
    const res = await fetch(`${API_BASE}/ndb-price?${qs.toString()}`, {
      headers: { Accept: "application/json" },
    });
    const body = (await res.json().catch(() => ({}))) as {
      ok?: boolean;
      matched?: boolean;
      price?: number;
      reference?: string | null;
      reason?: string;
    };
    if (!res.ok || body.ok === false) {
      return { matched: false, reason: body.reason || `lookup failed (HTTP ${res.status})`, key };
    }
    // From here the endpoint has answered, so the answer is worth keeping either way.
    if (!body.matched) {
      return remember({ matched: false, reason: body.reason || "no price on file", key });
    }
    const price = Number(body.price);
    if (!Number.isFinite(price) || price <= 0) {
      return remember({ matched: false, reason: "price list returned a non-positive price", key });
    }
    return remember({ matched: true, price, reference: body.reference ?? null, key });
  } catch (e) {
    return { matched: false, reason: e instanceof Error ? e.message : String(e), key };
  }
}

/** Human-readable key, for logs and the audit view: "HTB / ACLN / 30 / 34". */
export function describeNdbPriceKey(key: NdbPriceKey | null): string {
  return key ? `${key.housingType} / ${key.design} / ${key.size} / ${key.height}` : "(incomplete)";
}
